import React from "react";
import { Link } from "react-router-dom";
import { Heart, BedDouble, Bath, Ruler, ArrowRight, ShieldCheck, Share2 } from "lucide-react";
import { toast } from "sonner";
import { TrustScoreBadge } from "./TrustScoreWidget";
import { useSaveProperty } from "@/lib/useSaveProperty";

const badgeForType = (type) => {
    const map = {
        Villa: "LUXURY VILLA",
        Penthouse: "PRIVATE PENTHOUSE",
        Apartment: "PREMIUM APARTMENT",
        Commercial: "COMMERCIAL TOWER",
        Plot: "LAND PARCEL",
        Resort: "PREMIUM RESORTS",
    };
    return map[type] || "PREMIUM RESIDENCE";
};

// Optional per-property override — takes precedence over type-based badge.
const badgeFor = (item) => {
    const title = (item.title || "").toLowerCase();
    if (title.includes("resort")) return "PREMIUM RESORTS";
    return badgeForType(item.type);
};

// Some price_display strings already contain "onwards" — we render our own
// "Onwards" chip below, so strip it here to avoid duplication.
const stripOnwards = (s) => (s || "").replace(/\s*onwards\s*$/i, "").trim();

export default function PropertyCardV2({ item, index = 0 }) {
    const badge = badgeFor(item);
    const { saved, busy, toggle } = useSaveProperty(item.id);
    const cleanPrice = stripOnwards(item.price_display);

    const share = async (e) => {
        e.preventDefault(); e.stopPropagation();
        const url = `${window.location.origin}/properties/${item.slug || item.id}`;
        const text = `${item.title} — ${item.locality || ""}, ${item.city || ""}`;
        try {
            if (navigator.share) await navigator.share({ title: item.title, text, url });
            else { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
        } catch { /* user dismissed */ }
    };

    return (
        <Link
            to={`/properties/${item.slug || item.id}`}
            data-testid={`property-card-v2-${item.slug || item.id}`}
            className="group block bg-white border border-black/8 shadow-[0_1px_2px_rgba(0,0,0,0.04)] hover:shadow-[0_18px_40px_-20px_rgba(0,0,0,0.35)] transition-all duration-500"
            style={{ animationDelay: `${(index % 6) * 80}ms` }}
        >
            <div className="relative property-image aspect-[5/4] bg-black/5">
                <img src={item.cover_image} alt={item.title} className="w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                <span className="absolute top-4 left-4 overline text-[10px] tracking-[0.24em] bg-[var(--ebg-gold)] text-black px-2.5 py-1">
                    {badge}
                </span>
                {item.is_dealer && (
                    <span data-testid={`dealer-tag-${item.slug}`} className="absolute top-12 left-4 overline text-[10px] tracking-[0.2em] bg-black text-white px-2.5 py-1">Dealer Property</span>
                )}
                <button
                    type="button"
                    disabled={busy}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggle(); }}
                    data-testid={`property-fav-${item.slug}`}
                    className={`absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${saved ? "bg-[var(--ebg-gold)] text-black" : "bg-white/90 text-black/60 hover:text-red-500"}`}
                    aria-label={saved ? "Unsave" : "Save"}
                >
                    <Heart size={14} fill={saved ? "currentColor" : "none"} />
                </button>
                <button
                    type="button"
                    onClick={share}
                    data-testid={`property-share-${item.slug}`}
                    className="absolute top-14 right-4 w-8 h-8 rounded-full flex items-center justify-center bg-white/90 text-black/60 hover:text-[var(--ebg-gold)] transition-colors opacity-0 group-hover:opacity-100"
                    aria-label="Share"
                >
                    <Share2 size={13}/>
                </button>
                {/* Trust Score badge */}
                {item.trust_score?.total > 0 ? (
                    <div className="absolute bottom-3 right-3">
                        <TrustScoreBadge score={item.trust_score.total} />
                    </div>
                ) : null}
                {/* Legal verified pill */}
                {item.legal_status === "verified" ? (
                    <div className="absolute bottom-3 left-3 flex items-center gap-1 bg-black/70 backdrop-blur-md text-white px-2 py-1 text-[10px] uppercase tracking-[0.18em] font-bold">
                        <ShieldCheck size={11} className="text-[var(--ebg-gold)]"/>
                        Legally Verified
                    </div>
                ) : null}
            </div>
            <div className="p-5">
                <h3 className="font-serif text-[22px] leading-tight text-black">{item.title}</h3>
                <p className="text-black/55 text-sm mt-1">
                    {item.locality}, {item.city}
                    {item.developer ? <span className="text-black/40"> &middot; by {item.developer}</span> : null}
                </p>
                <div className="flex items-center gap-4 mt-4 text-[11px] text-black/60 tabular-nums flex-wrap">
                    {item.bhk ? (
                        <span className="flex items-center gap-1.5"><BedDouble size={12} className="text-[var(--ebg-gold)]"/> {item.bhk} Beds</span>
                    ) : null}
                    {item.bathrooms ? (
                        <span className="flex items-center gap-1.5"><Bath size={12} className="text-[var(--ebg-gold)]"/> {item.bathrooms} Baths</span>
                    ) : null}
                    {item.area_sqft ? (
                        <span className="flex items-center gap-1.5"><Ruler size={12} className="text-[var(--ebg-gold)]"/> {item.area_sqft.toLocaleString()} Sq.Ft.</span>
                    ) : null}
                </div>
                {/* Trust metadata line — RERA, possession/status */}
                {(item.rera_number || item.possession_date || item.status) && (
                    <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10.5px] uppercase tracking-[0.14em] text-black/50">
                        {item.rera_number && <span data-testid={`card-rera-${item.slug}`}>RERA · {item.rera_number}</span>}
                        {item.possession_date && <span>&middot; Possession {item.possession_date}</span>}
                        {!item.possession_date && item.status && <span>&middot; {item.status}</span>}
                    </div>
                )}
                <div className="mt-4 pt-4 border-t border-black/8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                    <div className="min-w-0">
                        <div className="font-serif text-[18px] sm:text-[20px] md:text-[22px] text-black tabular-nums break-words">{cleanPrice}</div>
                        <div className="overline text-[9px] text-black/40 mt-0.5">
                            Onwards
                            {item.price_per_sqft ? <span className="normal-case ml-2 text-black/55">· EMI from ₹{Math.round(item.price_per_sqft * (item.area_sqft || 0) * 0.0067 / 1000)}K/mo*</span> : null}
                        </div>
                    </div>
                    <span className="overline text-[10px] text-[var(--ebg-gold)] border border-[var(--ebg-gold-line)] px-3 py-2 group-hover:bg-[var(--ebg-gold)] group-hover:text-black transition-colors inline-flex items-center justify-center gap-1.5 shrink-0 self-start sm:self-auto">
                        View Details <ArrowRight size={11}/>
                    </span>
                </div>
            </div>
        </Link>
    );
}
